/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\GatewaySipProfileSettingsTabs.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Gateway SIP Profiles module.
----------------------------------------------------------------*/


import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';

interface CategoryDef {
  id: number;
  key: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface FieldDefinition {
  name: string;
  fsName?: string;
  type: string;
  default: string | number | boolean;
  options?: string[];
  possibleValues?: string;
  min?: number;
  max?: number;
  optional?: boolean;
  dependsOn?: string;
  placeholder?: string;
  hidden?: boolean;
  videoOnly?: boolean;
}

interface GatewaySipProfileSettingsTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  categories: CategoryDef[];
  fieldConfig: Record<string, FieldDefinition[]>;
  searchQuery: string;
  searchResults: Record<string, number>;
  renderField: (field: FieldDefinition) => React.ReactNode;
  t: (key: string, options?: { count?: number; defaultValue?: string }) => string;
}

export function GatewaySipProfileSettingsTabs({
  activeTab, onTabChange, categories, fieldConfig,
  searchQuery, searchResults, renderField, t,
}: GatewaySipProfileSettingsTabsProps) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <Tabs value={activeTab} onValueChange={onTabChange}>
        {/* Tabs List */}
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap">
          {categories.map((category) => {
            const Icon = category.icon;
            const label = t(`gatewaySipProfiles:${category.labelKey}`);
            const hasResults = searchQuery.trim() && searchResults[category.key] > 0;
            const resultCount = searchResults[category.key] || 0;

            return (
              <TabsTrigger
                key={category.key}
                value={category.key}
                icon={<Icon className="w-4 h-4" />}
                className={hasResults ? 'relative' : ''}
              >
                <span className="flex items-center gap-2">
                  {label}
                  {hasResults && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-green-500 rounded-full">
                      {resultCount}
                    </span>
                  )}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Tabs Content */}
        {categories.map((category) => {
          const categoryFields = fieldConfig[category.key] || [];

          return (
            <TabsContent key={category.key} value={category.key}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                {categoryFields
                  .filter((field) => {
                    if (field.hidden) return true;
                    if (!searchQuery) return true;

                    const fieldLabel = t(`gatewaySipProfiles:${field.name}`);
                    const fieldFsName = field.fsName || '';
                    const searchLower = searchQuery.toLowerCase();

                    return (
                      fieldLabel.toLowerCase().includes(searchLower) ||
                      fieldFsName.toLowerCase().includes(searchLower) ||
                      field.name.toLowerCase().includes(searchLower)
                    );
                  })
                  .map((field) => {
                    if (field.hidden) {
                      return (
                        <div key={field.name} style={{ display: 'none' }}>
                          {renderField(field)}
                        </div>
                      );
                    }
                    return (
                      <React.Fragment key={field.name}>
                        {renderField(field)}
                      </React.Fragment>
                    );
                  })}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
